import { RouteLocation, LocationQuery } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAppReturnLinkStore } from '@/store/AppReturnLinkStore'
import { useAuthStore } from '@/store/AuthStore'
import { useBrazeStore } from '@/store/BrazeStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useCouponStore } from '@/store/CouponStore'
import { useDownloadStore } from '@/store/DownloadStore'
import { useFeatureFlagStore } from '@/store/FeatureFlagStore'
import { useOfferStore } from '@/store/OfferStore'
import { usePlanStore } from '@/store/PlanStore'
import { useProductStore } from '@/store/ProductStore'
import { useRecurlyStore } from '@/store/RecurlyStore'
import { useScreenStore } from '@/store/ScreenStore'
import { useSubscriptionStore } from '@/store/SubscriptionStore'
import { useSurveyStore } from '@/store/SurveyStore'
import { useUserStore } from '@/store/UserStore'
import { useContentStore } from '@/store/ContentStore'
import { ProductId, UserType } from '@/types'
import { useAmplitude, useGtm, BrazeEvents } from '@outputinc/o-analytics'
import { useTrialStatus } from '@/composables/useTrialStatus'

export const checkAlreadyHas = (to: RouteLocation) => {
  const checkoutStore = useCheckoutStore()
  const { alreadyHas } = storeToRefs(checkoutStore)

  if (!alreadyHas.value) {
    return {
      ...to,
      name: 'checkout'
    }
  }
}

export const checkShouldRedirectToUpgrade = (to: RouteLocation) => {
  const { query } = to
  const subscriptionStore = useSubscriptionStore()
  const { resetSelectedPlan } = usePlanStore()
  const { canUpgrade } = storeToRefs(subscriptionStore)

  if (canUpgrade.value) {
    resetSelectedPlan()

    return {
      name: 'upgrade',
      query,
      params: {
        productName: ProductId.OutputOne
      }
    }
  }
}

export const handleOrphanUpgradePath = (to: RouteLocation) => {
  if (!to.params.productName) {
    return {
      ...to,
      name: 'upgrade',
      params: {
        productName: ProductId.OutputOne
      }
    }
  }
}

export const handleOrphanOfferPath = (to: RouteLocation) => {
  const offerStore = useOfferStore()

  const { currentOffers } = storeToRefs(offerStore)

  if (!to.params.offerId) {
    const [offer] = currentOffers.value

    if (offer) {
      return {
        ...to,
        name: 'offer',
        params: {
          offerId: offer.id
        }
      }
    }

    return {
      ...to,
      name: 'upgrade',
      params: {
        productName: ProductId.OutputOne
      }
    }
  }
}

export const checkCanUpgrade = (to: RouteLocation) => {
  const { query } = to
  const subscriptionStore = useSubscriptionStore()
  const { canUpgrade } = storeToRefs(subscriptionStore)

  if (!canUpgrade.value) {
    return {
      name: 'checkout',
      query,
      params: {
        productName: ProductId.OutputOne
      }
    }
  }
}

export const setCheckoutSubscriptionId = () => {
  const { setCheckoutProperty } = useCheckoutStore()
  const subscriptionStore = useSubscriptionStore()
  const { mostRecentSubscriptionId: subscriptionId } = storeToRefs(subscriptionStore)

  setCheckoutProperty({
    subscriptionId
  })
}

export const handleQueryParameters = (to: RouteLocation) => {
  const { query: toQuery } = to
  const { setCheckoutProperty } = useCheckoutStore()
  const { setAppReferrer } = useAppReturnLinkStore()

  const { setCouponCode } = useCouponStore()

  const { planCode, appReferrer, code } = toQuery

  if (planCode) {
    setCheckoutProperty({ planCode })
  }

  if (appReferrer) {
    setAppReferrer(appReferrer)
  }

  if (code) {
    setCouponCode(code)
  }
}

export const setSelectedPlan = () => {
  const checkoutStore = useCheckoutStore()
  const { handlePlanCode } = usePlanStore()
  const { planCode } = storeToRefs(checkoutStore)

  if (planCode.value) {
    handlePlanCode(planCode.value)
  }
}

export const fetchPlans = async () => {
  const { fetchPlans } = usePlanStore()

  await fetchPlans()
}

export const checkHasSelectedPlan = async (to: RouteLocation) => {
  const { query: toQuery } = to
  const planStore = usePlanStore()
  const { selectedPlan } = storeToRefs(planStore)
  const { setPlanForProduct } = planStore

  if (!selectedPlan.value) {
    setPlanForProduct()

    const { planCode, ...query } = toQuery

    return {
      ...to,
      query
    }
  }
}

export const initializeUser = (to: RouteLocation) => {
  const { query, redirectedFrom } = to
  const { initialize: initializeUser } = useUserStore()
  const fromCreateAccount = query.from === 'create' || redirectedFrom?.query?.from === 'create'

  const type = fromCreateAccount ? UserType.New : UserType.Existing

  initializeUser(type)

  if (query.from) {
    const { from, ...remainingQuery } = query
    return { ...to, query: remainingQuery }
  }
}

export const fetchUserGeoLocation = () => {
  const { fetchUserLocation } = useUserStore()

  fetchUserLocation()
}

export const setProduct = async (to: RouteLocation) => {
  const { params, ...route } = to
  const { setProduct: setStoreProduct } = useProductStore()

  if (!params.productName) {
    return {
      ...route,
      name: 'checkout',
      params: {
        productName: ProductId.OutputOne
      }
    }
  }

  await setStoreProduct(params.productName)
}

export const getRedirectUrl = (query: LocationQuery, baseUrl: string) => {
  const isQuery = Object.entries(query).length !== 0
  const queryString = Object.keys(query)
    .map(key => `${key}=${query[key]}`)
    .join('&')
  const externalUrl = isQuery ? `${baseUrl}?${queryString}` : baseUrl
  return externalUrl
}

export const redirectToLoginService = (to: RouteLocation) => {
  // grab the path
  const path = to.path
  // grab the hostname
  const host = encodeURI(`${window.location.protocol}//${window.location.host}`)
  // grab the rest of the query params if any
  const redirectQuery = {
    ...to.query,
    host
  }

  const target = `${import.meta.env.VITE_LOGIN_HOSTNAME}${path}`

  const loginUrl = getRedirectUrl(redirectQuery, target)

  window.location.replace(loginUrl)
}

export const resetUserAuth = () => {
  const { clearTokens } = useAuthStore()
  const { resetUser } = useUserStore()
  const { resetScreenSessionState } = useScreenStore()

  clearTokens()
  resetUser()
  resetScreenSessionState()
}

export const removeReferrer = (to: RouteLocation) => {
  const { query, path } = to
  if (query.referrer) {
    delete query.referrer

    return { path, query }
  }
}

export const fetchSurveys = async () => {
  const { fetchSurveys: fetchSurveysQuery } = useSurveyStore()

  await fetchSurveysQuery()
}

export const setSurveyId = (to: RouteLocation) => {
  const { params } = to
  const { setSurveyId: setCurrentSurveyId } = useSurveyStore()

  if (params.surveyId) {
    setCurrentSurveyId(params.surveyId)
  }
}

export const initializeScreens = (to: RouteLocation) => {
  const { meta } = to

  const { setScreens } = useScreenStore()

  if (meta?.screenSet) {
    setScreens(meta.screenSet)
  }
}

export const setScreen = (to: RouteLocation) => {
  const screenStore = useScreenStore()

  const { restoreScreens } = screenStore
  const { currentScreen } = storeToRefs(screenStore)

  restoreScreens()

  if (currentScreen.value?.id !== to.name) {
    const params = {
      ...to.params,
      ...currentScreen.value?.params
    }

    return {
      ...to,
      name: currentScreen.value?.id,
      params
    }
  }
}

export const fetchDownloads = () => {
  const { fetchClientReleaseDownloads } = useDownloadStore()

  fetchClientReleaseDownloads()
}

export const fetchSubscriptions = async () => {
  const { fetchSubscriptions } = useSubscriptionStore()

  await fetchSubscriptions()
}

export const fetchSubscriptionsAccount = async () => {
  const { fetchSubscriptionsAccount } = useUserStore()

  await fetchSubscriptionsAccount()
}

export const checkPlanProduct = (to: RouteLocation) => {
  const { query, params: currentParams } = to

  const productStore = useProductStore()
  const planStore = usePlanStore()
  const { productId } = storeToRefs(productStore)
  const { resetProduct } = productStore
  const { selectedPlanProduct } = storeToRefs(planStore)

  if (productId.value !== selectedPlanProduct.value) {
    resetProduct()

    return {
      name: 'checkout',
      query,
      params: {
        ...currentParams,
        productName: selectedPlanProduct.value
      }
    }
  }
}

export const checkRedirectToAlreadyHas = (to: RouteLocation) => {
  const { query, params: toParams } = to

  const subscriptionStore = useSubscriptionStore()
  const productStore = useProductStore()
  const offerStore = useOfferStore()

  const { setCheckoutProperty } = useCheckoutStore()
  const { hasActiveSubscription, hasActiveCanceledPausedSubscription, mostRecentSubscriptionProduct } = storeToRefs(subscriptionStore)
  const { productId } = storeToRefs(productStore)
  const { promotionalOfferProduct } = storeToRefs(offerStore)

  const hasOutputOnePlan = mostRecentSubscriptionProduct.value === ProductId.OutputOne || promotionalOfferProduct.value.includes(ProductId.OutputOne)

  const hasActiveOutputOneSubscription = hasActiveSubscription.value && hasOutputOnePlan

  const activeSubscriptionMatchesProduct = mostRecentSubscriptionProduct.value === productId.value
  const offerProductMatches = promotionalOfferProduct.value?.includes(productId.value) || promotionalOfferProduct.value?.includes(ProductId.OutputOne)

  const hasCurrentSubscriptionToSameProduct = hasActiveCanceledPausedSubscription.value && (activeSubscriptionMatchesProduct || offerProductMatches)

  const sendToAlreadyHas = hasCurrentSubscriptionToSameProduct || hasActiveOutputOneSubscription

  setCheckoutProperty({ alreadyHas: sendToAlreadyHas })

  if (sendToAlreadyHas) {
    return {
      name: 'existing',
      query,
      params: {
        ...toParams,
        productName: productId.value || ProductId.OutputOne
      }
    }
  }
}

export const setPlanFromCoupon = () => {
  const couponStore = useCouponStore()
  const planStore = usePlanStore()
  const { eligiblePlans } = storeToRefs(couponStore)
  const { selectedPlan } = storeToRefs(planStore)
  const { setSelectedPlan } = planStore

  if (!selectedPlan.value && eligiblePlans.value?.length > 0) {
    const [plan] = eligiblePlans.value

    setSelectedPlan(plan)
  }
}

export const checkHasUpgradePlan = () => {
  const planStore = usePlanStore()
  const { selectedPlanProduct } = storeToRefs(planStore)
  const { resetSelectedPlan } = planStore
  // Some day we may have other upgrade products, but for now we only have Output One
  if (selectedPlanProduct.value !== ProductId.OutputOne) {
    resetSelectedPlan()
  }
}

export const checkHasSelectedNfrPlan = () => {
  const planStore = usePlanStore()
  const { selectedPlan, planFromNfrPlanSet } = storeToRefs(planStore)
  const { setSelectedPlan } = planStore

  if (!selectedPlan.value && planFromNfrPlanSet.value) {
    setSelectedPlan(planFromNfrPlanSet.value)
  }
}

export const setProductFromPlan = (to: RouteLocation) => {
  const { params } = to
  const { setProduct: setStoreProduct } = useProductStore()
  const planStore = usePlanStore()
  const { selectedPlanProduct } = storeToRefs(planStore)

  setStoreProduct(selectedPlanProduct.value)

  if (!params.productName) {
    return {
      ...to,
      params: {
        ...params,
        productName: selectedPlanProduct.value
      }
    }
  }
}

export const resetCheckout = () => {
  const { resetCheckoutState } = useCheckoutStore()

  resetCheckoutState()
}

export const fetchCoupon = async () => {
  const couponStore = useCouponStore()
  const { fetchCoupon } = couponStore
  const { couponCode } = storeToRefs(couponStore)
  if (couponCode?.value) {
    await fetchCoupon(couponCode.value)
  }
}

export const applyCouponToCheckout = () => {
  const couponStore = useCouponStore()
  const { couponCode } = storeToRefs(couponStore)

  const { applyCouponToCheckout: applyToCheckout } = couponStore

  if (couponCode.value) {
    applyToCheckout(couponCode.value)
  }
}

export const removeInvalidCoupon = (to: RouteLocation) => {
  const { query, ...location } = to
  const couponStore = useCouponStore()
  const { couponCode } = storeToRefs(couponStore)
  const { code, ...newQuery } = query

  // if there is a code in the url but no coupon in the store
  // remove the code from the url
  if (code && !couponCode.value) {
    delete newQuery.code

    return {
      ...location,
      query: newQuery
    }
  }
}

export const checkCouponCanBeAppliedToPlan = () => {
  const couponStore = useCouponStore()
  const planStore = usePlanStore()

  const { selectedPlanCode } = storeToRefs(planStore)
  const { coupon } = storeToRefs(couponStore)
  const { checkCouponCanBeAppliedToPlan } = couponStore

  if (coupon.value) {
    checkCouponCanBeAppliedToPlan(coupon.value, selectedPlanCode.value)
  }
}

export const setNoPaymentRequiredCheckout = (to: RouteLocation) => {
  const { params } = to
  const { setCheckoutProperty } = useCheckoutStore()
  const { noPaymentPlanType } = params

  if (noPaymentPlanType) {
    setCheckoutProperty({ noPaymentPlanType })
  }
}

export const checkCanNoPaymentRequiredCheckout = (to: RouteLocation) => {
  const { params } = to
  const subscriptionStore = useSubscriptionStore()
  const couponStore = useCouponStore()
  const planStore = usePlanStore()
  const productStore = useProductStore()
  const { hasActiveSubscription } = storeToRefs(subscriptionStore)
  const { isRedeemable } = storeToRefs(couponStore)
  const { selectedPlan } = storeToRefs(planStore)
  const { productId } = storeToRefs(productStore)

  const canNoPaymentCheckout = !hasActiveSubscription.value && isRedeemable.value && selectedPlan.value

  if (!canNoPaymentCheckout) {
    return {
      ...to,
      name: 'checkout',
      params: {
        ...params,
        productName: productId.value || ProductId.OutputOne
      }
    }
  }
}

export const fetchCustomerSurveyResponses = async () => {
  const { fetchSurveyResponses } = useSurveyStore()
  await fetchSurveyResponses()
}

export const fetchFeatureFlags = async () => {
  const { fetchFlags } = useFeatureFlagStore()

  await fetchFlags()
}

export const provideStoreState = () => {
  useProductStore()
}

export const setOffer = (to: RouteLocation) => {
  const { params } = to
  const { setOfferId } = useOfferStore()

  const { offerId } = params

  if (offerId) {
    setOfferId(params.offerId)
  }
}

export const setOfferProduct = () => {
  const offerStore = useOfferStore()
  const { activeOfferProduct } = storeToRefs(offerStore)

  const { setProduct: setStoreProduct } = useProductStore()
  if (activeOfferProduct.value) {
    setStoreProduct(activeOfferProduct.value)
  }
}

export const setOfferCoupon = () => {
  const offerStore = useOfferStore()
  const { setCouponCode } = useCouponStore()
  const { activeOfferCoupons } = storeToRefs(offerStore)

  if (activeOfferCoupons.value) {
    setCouponCode(activeOfferCoupons.value)
  }
}

export const setOfferPlan = () => {
  const offerStore = useOfferStore()
  const { activeOfferPlanFromLastInterval } = storeToRefs(offerStore)
  const { setSelectedPlan } = usePlanStore()

  if (activeOfferPlanFromLastInterval.value) {
    setSelectedPlan(activeOfferPlanFromLastInterval.value)
  }
}

export const fetchActiveOffer = () => {
  const { handleCouponCode } = useCouponStore()

  const offerStore = useOfferStore()
  const { activeOfferCoupons } = storeToRefs(offerStore)
  if (activeOfferCoupons.value) {
    // if there is an active promotion coupon
    handleCouponCode(activeOfferCoupons.value)
  }
}

export const fetchCoupons = () => {
  const { fetchCoupons } = useCouponStore()

  fetchCoupons()
}

export const checkIsEligibleForOffer = (to: RouteLocation) => {
  const { query } = to
  const offerStore = useOfferStore()

  const { eligibleOfferId, hasEligibleOffer } = storeToRefs(offerStore)

  if (hasEligibleOffer.value) {
    if (eligibleOfferId.value) {
      return {
        name: 'offer',
        query,
        params: {
          offerId: eligibleOfferId.value
        }
      }
    }
  }
}

// Kick off the recurly.js load as the user enters a flow that will need it (checkout, upgrade,
// offer). Loading here — rather than on the payment screen — gives the script time to arrive and
// configure before the credit-card fields render. The store guards against duplicate loads.
export const loadRecurly = (): void => {
  useRecurlyStore().loadRecurly()
}

export const initializeCheckout = (to: RouteLocation): void => {
  const { path, matched } = to
  const [type] = matched.map(route => route.name)
  const { setUserId } = useAmplitude()
  const { track: gtmTrackEvent, Events: GtmEvents } = useGtm()

  const checkoutStore = useCheckoutStore()
  const userStore = useUserStore()
  const productStore = useProductStore()
  const planStore = usePlanStore()
  const { postIdentifyEmailUser: identifyEmailUser, trackEmailUser } = useBrazeStore()
  const { setCheckoutProperty } = checkoutStore
  const { isActive } = storeToRefs(checkoutStore)
  const { outputUid, userType, email } = storeToRefs(userStore)
  const { productId } = storeToRefs(productStore)
  const { selectedPlanInterval, selectedPlanCode, selectedPlanProduct } = storeToRefs(planStore)

  if (!isActive.value) {
    setUserId(outputUid.value)

    identifyEmailUser({
      userAlias: {
        aliasLabel: 'email',
        aliasName: email.value
      },
      externalId: outputUid.value
    })

    trackEmailUser({
      name: BrazeEvents.WEBSITE_CHECKOUT_START_RECURLY,
      externalId: outputUid.value,
      time: new Date().toISOString(),
      properties: {
        external_id: outputUid.value,
        billing_interval: selectedPlanInterval.value,
        plan_code: selectedPlanCode.value,
        products: selectedPlanProduct.value,
        path
      }
    })

    gtmTrackEvent(GtmEvents.CHECKOUT_SUBSCRIPTION_INTAKE, {
      userType: userType.value,
      products: [productId.value],
      email: email.value
    })

    setCheckoutProperty({
      type,
      isActive: true
    })
  }
}

export const checkEligibleForActiveOffer = (to: RouteLocation) => {
  const { query } = to
  const offerStore = useOfferStore()
  const { isEligibleForActiveOffer, activeOfferProduct } = storeToRefs(offerStore)

  if (!isEligibleForActiveOffer.value) {
    return {
      name: 'checkout',
      query,
      params: {
        productName: activeOfferProduct.value
      }
    }
  }
}

export const fetchOfferContent = async () => {
  const { fetchOfferContent } = useContentStore()
  const offerStore = useOfferStore()
  const { activeOfferId } = storeToRefs(offerStore)

  await fetchOfferContent(`offers/${activeOfferId.value}`)
}

export const fetchUpgradeContent = async () => {
  const { fetchUpgradeContent } = useContentStore()

  await fetchUpgradeContent('upgrade/output-one')
}

export const checkoutStartEvent = (to: RouteLocation) => {
  const { path } = to
  const checkoutStore = useCheckoutStore()
  const authStore = useAuthStore()
  const productStore = useProductStore()
  const planStore = usePlanStore()

  const { hasCheckoutStart } = storeToRefs(checkoutStore)
  const { setCheckoutProperty } = checkoutStore
  const { isUserAuthenticated } = storeToRefs(authStore)
  const { productId } = storeToRefs(productStore)
  const { selectedPlanProduct, selectedPlanCode } = storeToRefs(planStore)

  const checkoutStartProduct = selectedPlanProduct.value || productId.value

  // check the current product against what is in the store
  if (hasCheckoutStart.value === checkoutStartProduct) {
    return
  }

  const { track, Events } = useAmplitude()

  track(Events.CHECKOUT_SUBSCRIPTION_START, {
    userIsAuthenticated: isUserAuthenticated.value,
    products: [checkoutStartProduct],
    path,
    planCode: selectedPlanCode.value,
    isTrialTimeline: true
  })

  setCheckoutProperty({ hasCheckoutStart: checkoutStartProduct })
}

// Composes the trial timeline screen in or out of the checkout flow based on whether the plan has a
// trial. Runs late in the chain (after the plan/coupon are resolved) so planHasTrial is reliable.
export const setTrialTimelineScreen = () => {
  const { planHasTrial } = useTrialStatus()
  const { setTrialTimelineVisible } = useScreenStore()

  setTrialTimelineVisible(planHasTrial.value)
}

export const checkoutShouldShowTrialTimeline = (to: RouteLocation) => {
  const { query, params } = to
  const { planHasTrial } = useTrialStatus()

  // No-trial plans don't include the trial timeline screen at all (see setTrialTimelineScreen), so
  // this only fires if someone lands on /trial-timeline directly — send them to the payment step.
  if (!planHasTrial.value) {
    return {
      name: 'payment-method',
      query,
      params
    }
  }
}
